import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { useGetTransactionsQuery } from '../../store/api/adminApi';
import { useLanguage } from '../../hooks/useLanguage';
import { formatDate } from '../../utils/dateFormatter';
import { scrubProviderBrands } from '../../utils/serverDisplayNames';
import { IS_TENANT_MODE } from '../../config/tenantMode';

const fieldCls =
  'w-full px-3 py-2 rounded-lg text-sm font-body text-neutral-light bg-[#0a1220] border border-slate-600/50 focus:outline-none focus:border-brand-accent/70 placeholder:text-slate-500';
const labelCls = 'block text-xs font-medium text-slate-400 mb-1.5 font-body';

export function AdminTransactionsPage() {
  const { t } = useTranslation();
  const showCashback = !IS_TENANT_MODE;
  const { currentLanguage } = useLanguage();
  const [searchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('userId') ?? '';

  const [page, setPage] = useState(1);
  const [selectedTx, setSelectedTx] = useState<any | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const limit = 20;

  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('');
  const [userIdFilter, setUserIdFilter] = useState<string>(userIdFromUrl);

  useEffect(() => {
    if (userIdFromUrl && userIdFilter !== userIdFromUrl) {
      setUserIdFilter(userIdFromUrl);
      setPage(1);
    }
  }, [userIdFromUrl]);

  const [currencyFilter, setCurrencyFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [minAmountFilter, setMinAmountFilter] = useState<string>('');
  const [maxAmountFilter, setMaxAmountFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<'createdAt' | 'updatedAt' | 'amount' | 'type' | 'status'>('createdAt');
  const [sortOrder, setSortOrder] = useState<-1 | 1>(-1);

  const { data, isLoading, error, refetch, isFetching } = useGetTransactionsQuery({
    page,
    limit,
    type: typeFilter || undefined,
    status: statusFilter as 'pending' | 'completed' | 'failed' | 'reversed' | undefined,
    paymentMethod: paymentMethodFilter || undefined,
    userId: userIdFilter || undefined,
    currency: currencyFilter || undefined,
    dateFrom: dateFromFilter || undefined,
    dateTo: dateToFilter || undefined,
    minAmount: minAmountFilter ? parseFloat(minAmountFilter) : undefined,
    maxAmount: maxAmountFilter ? parseFloat(maxAmountFilter) : undefined,
    search: searchFilter || undefined,
    sortBy,
    sortOrder,
  });

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleResetFilters = () => {
    setTypeFilter('');
    setStatusFilter('');
    setPaymentMethodFilter('');
    setUserIdFilter('');
    setCurrencyFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setMinAmountFilter('');
    setMaxAmountFilter('');
    setSearchFilter('');
    setSortBy('createdAt');
    setSortOrder(-1);
    setPage(1);
  };

  const activeFilterCount = [
    typeFilter, statusFilter, paymentMethodFilter, userIdFilter, currencyFilter,
    dateFromFilter, dateToFilter, minAmountFilter, maxAmountFilter, searchFilter,
  ].filter(Boolean).length;

  const getTransactionTypeLabel = (type: string, paymentDetails?: Record<string, any>) => {
    if (type === 'purchase' && (paymentDetails?.type === 'renewal' || paymentDetails?.isRenewal === true)) {
      return t('admin.transactions.types.purchase_renewal', { defaultValue: 'شراء (تجديد)' });
    }
    return t(`admin.transactions.types.${type}`, { defaultValue: type });
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25';
      case 'pending': return 'bg-amber-500/15 text-amber-400 border border-amber-500/25';
      case 'failed': return 'bg-red-500/15 text-red-400 border border-red-500/25';
      case 'reversed': return 'bg-orange-500/15 text-orange-400 border border-orange-500/25';
      default: return 'bg-slate-500/15 text-slate-400 border border-slate-500/25';
    }
  };

  const getAmountColor = (amount: number) => {
    if (amount > 0) return 'text-emerald-400';
    if (amount < 0) return 'text-red-400';
    return 'text-slate-400';
  };

  if (isLoading) {
    return (
      <div className="py-16 text-center">
        <p className="text-slate-400 text-lg font-body animate-pulse">{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-glass rounded-2xl p-8 text-center">
        <h2 className="text-xl font-heading font-bold text-danger mb-3">{t('admin.transactions.error.title')}</h2>
        <p className="text-slate-400 font-body mb-5">
          {'data' in (error as { data?: { message?: string } })
            ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
            : t('errors.network')}
        </p>
        <button
          onClick={() => void refetch()}
          className="px-5 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  const transactions = data?.data.transactions || [];
  const pagination = data?.data.pagination;

  return (
    <div className="space-y-4 pb-6 animate-slide-up">
      {/* Compact header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-brand-accent leading-tight">
            {t('admin.transactions.title')}
          </h1>
          <p className="text-slate-400 text-sm font-body mt-1">
            {t('admin.transactions.subtitle')}
            {pagination?.total != null && (
              <span className="ms-2 text-slate-500">· {pagination.total}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className={`px-3 py-2 rounded-lg text-sm font-heading font-semibold border transition-colors ${
              filtersOpen || activeFilterCount
                ? 'border-brand-accent/40 bg-brand-accent/10 text-brand-accent'
                : 'border-slate-600/50 bg-[#0a1220] text-slate-300 hover:border-slate-500'
            }`}
          >
            {t('admin.transactions.filters.type', { defaultValue: 'فلاتر' })}
            {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="px-3 py-2 rounded-lg text-sm font-heading font-semibold border border-slate-600/50 bg-[#0a1220] text-slate-300 hover:border-slate-500 disabled:opacity-50"
          >
            {t('common.retry', { defaultValue: 'تحديث' })}
          </button>
        </div>
      </div>

      {/* Quick bar — always visible */}
      <div className="card-glass rounded-xl p-3 sm:p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className={labelCls}>{t('admin.transactions.filters.search')}</label>
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => { setSearchFilter(e.target.value); setPage(1); }}
              placeholder={t('admin.transactions.filters.searchPlaceholder')}
              className={fieldCls}
            />
          </div>
          <div>
            <label className={labelCls}>{t('admin.transactions.filters.type')}</label>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
              className={fieldCls}
            >
              <option value="">{t('admin.transactions.filters.all')}</option>
              <option value="recharge">{t('admin.transactions.types.recharge')}</option>
              <option value="purchase">{t('admin.transactions.types.purchase')}</option>
              <option value="renewal">{t('admin.transactions.types.purchase_renewal')}</option>
              <option value="referral_bonus">{t('admin.transactions.types.referral_bonus')}</option>
              <option value="refund">{t('admin.transactions.types.refund')}</option>
              <option value="admin_add">{t('admin.transactions.types.admin_add')}</option>
              <option value="admin_deduct">{t('admin.transactions.types.admin_deduct')}</option>
              {showCashback && (
                <>
                  <option value="cashback_claim">{t('admin.transactions.types.cashback_claim')}</option>
                  <option value="cashback_earn">{t('admin.transactions.types.cashback_earn')}</option>
                </>
              )}
            </select>
          </div>
          <div>
            <label className={labelCls}>{t('admin.transactions.filters.status')}</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className={fieldCls}
            >
              <option value="">{t('admin.transactions.filters.all')}</option>
              <option value="completed">{t('admin.transactions.status.completed')}</option>
              <option value="pending">{t('admin.transactions.status.pending')}</option>
              <option value="failed">{t('admin.transactions.status.failed')}</option>
              <option value="reversed">{t('admin.transactions.status.reversed')}</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>{t('admin.transactions.filters.userId')}</label>
            <input
              type="text"
              value={userIdFilter}
              onChange={(e) => { setUserIdFilter(e.target.value); setPage(1); }}
              placeholder={t('admin.transactions.filters.userIdPlaceholder')}
              className={fieldCls}
            />
          </div>
        </div>

        {filtersOpen && (
          <div className="mt-3 pt-3 border-t border-slate-700/50 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className={labelCls}>{t('admin.transactions.filters.paymentMethod')}</label>
              <input type="text" value={paymentMethodFilter} onChange={(e) => { setPaymentMethodFilter(e.target.value); setPage(1); }} className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>{t('admin.transactions.filters.currency')}</label>
              <input type="text" value={currencyFilter} onChange={(e) => { setCurrencyFilter(e.target.value); setPage(1); }} className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>{t('admin.transactions.filters.dateFrom')}</label>
              <input type="date" value={dateFromFilter} onChange={(e) => { setDateFromFilter(e.target.value); setPage(1); }} className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>{t('admin.transactions.filters.dateTo')}</label>
              <input type="date" value={dateToFilter} onChange={(e) => { setDateToFilter(e.target.value); setPage(1); }} className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>{t('admin.transactions.filters.minAmount')}</label>
              <input type="number" value={minAmountFilter} onChange={(e) => { setMinAmountFilter(e.target.value); setPage(1); }} step="0.01" className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>{t('admin.transactions.filters.maxAmount')}</label>
              <input type="number" value={maxAmountFilter} onChange={(e) => { setMaxAmountFilter(e.target.value); setPage(1); }} step="0.01" className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>{t('admin.transactions.filters.sortBy')}</label>
              <select value={sortBy} onChange={(e) => { setSortBy(e.target.value as typeof sortBy); setPage(1); }} className={fieldCls}>
                <option value="createdAt">{t('admin.transactions.sort.createdAt')}</option>
                <option value="updatedAt">{t('admin.transactions.sort.updatedAt')}</option>
                <option value="amount">{t('admin.transactions.sort.amount')}</option>
                <option value="type">{t('admin.transactions.sort.type')}</option>
                <option value="status">{t('admin.transactions.sort.status')}</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>{t('admin.transactions.filters.sortOrder')}</label>
              <select value={sortOrder} onChange={(e) => { setSortOrder(parseInt(e.target.value) as -1 | 1); setPage(1); }} className={fieldCls}>
                <option value="-1">{t('admin.transactions.sort.descending')}</option>
                <option value="1">{t('admin.transactions.sort.ascending')}</option>
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
              <button type="button" onClick={handleResetFilters} className="px-4 py-2 rounded-lg text-sm bg-slate-800 text-slate-200 border border-slate-600/50 hover:bg-slate-700">
                {t('admin.transactions.filters.reset')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* List */}
      {transactions.length === 0 ? (
        <div className="card-glass rounded-xl p-10 text-center">
          <p className="text-slate-400 font-body">{t('admin.transactions.empty')}</p>
        </div>
      ) : (
        <>
          <div className="hidden md:block card-glass rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700/60 bg-[#0a1220]/50">
                    {[
                      'id', 'userId', 'type', 'amount', 'status', 'paymentMethod', 'notes', 'created',
                    ].map((col) => (
                      <th key={col} className="px-3 py-3 text-start text-xs font-heading font-semibold text-brand-accent whitespace-nowrap">
                        {t(`admin.transactions.table.${col}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/80">
                  {transactions.map((transaction) => (
                    <tr
                      key={transaction._id}
                      onClick={() => setSelectedTx(transaction)}
                      className="hover:bg-white/[0.03] transition-colors cursor-pointer"
                    >
                      <td className="px-3 py-3">
                        <Link
                          to={`/admin/users/${transaction.user ?? transaction.userObjectId ?? transaction.userId}`}
                          className="text-brand-accent font-mono text-xs hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {transaction._id.slice(-8)}
                        </Link>
                      </td>
                      <td className="px-3 py-3 text-slate-300 font-body text-xs">{transaction.userId}</td>
                      <td className="px-3 py-3 text-slate-200 font-body">
                        {getTransactionTypeLabel(transaction.type, transaction.paymentDetails)}
                      </td>
                      <td className={`px-3 py-3 font-heading font-semibold ${getAmountColor(transaction.amount)}`}>
                        {transaction.amount > 0 ? '+' : ''}
                        {transaction.amount.toFixed(2)} {transaction.currency}
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-body ${getStatusBadgeColor(transaction.status)}`}>
                          {t(`admin.transactions.status.${transaction.status}`)}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-slate-400 font-body text-xs">{transaction.paymentMethod || '—'}</td>
                      <td className="px-3 py-3 text-slate-400 font-body text-xs max-w-[180px] truncate" title={scrubProviderBrands(transaction.notes) || undefined}>
                        {scrubProviderBrands(transaction.notes) || '—'}
                      </td>
                      <td className="px-3 py-3 text-slate-400 font-body text-xs whitespace-nowrap">
                        {formatDate(transaction.createdAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="md:hidden space-y-3">
            {transactions.map((transaction) => (
              <div
                key={transaction._id}
                onClick={() => setSelectedTx(transaction)}
                className="card-glass rounded-xl p-4 space-y-2 cursor-pointer active:scale-[0.99] transition-transform"
              >
                <div className="flex justify-between items-start gap-2">
                  <span className="text-xs text-slate-500 font-mono">{transaction._id.slice(-8)}</span>
                  <span className={`text-sm font-heading font-semibold ${getAmountColor(transaction.amount)}`}>
                    {transaction.amount > 0 ? '+' : ''}
                    {transaction.amount.toFixed(2)} {transaction.currency}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-slate-200">{getTransactionTypeLabel(transaction.type, transaction.paymentDetails)}</span>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${getStatusBadgeColor(transaction.status)}`}>
                    {t(`admin.transactions.status.${transaction.status}`)}
                  </span>
                </div>
                {transaction.notes && (
                  <p className="text-xs text-slate-400 line-clamp-2">{scrubProviderBrands(transaction.notes)}</p>
                )}
                <p className="text-xs text-slate-500">
                  {formatDate(transaction.createdAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus')}
                </p>
              </div>
            ))}
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => handlePageChange(page - 1)}
                className="px-3 py-1.5 rounded-lg text-sm border border-slate-600/50 text-slate-300 disabled:opacity-40"
              >
                ‹
              </button>
              <span className="text-sm text-slate-400 font-body">
                {page} / {pagination.totalPages}
              </span>
              <button
                type="button"
                disabled={page >= pagination.totalPages}
                onClick={() => handlePageChange(page + 1)}
                className="px-3 py-1.5 rounded-lg text-sm border border-slate-600/50 text-slate-300 disabled:opacity-40"
              >
                ›
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail modal — keep existing behavior */}
      {selectedTx && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
          onClick={() => setSelectedTx(null)}
        >
          <div
            className="card-glass w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <h3 className="text-lg font-heading font-bold text-brand-accent">
                {t('admin.transactions.title')}
              </h3>
              <button type="button" onClick={() => setSelectedTx(null)} className="text-slate-400 hover:text-white text-xl leading-none">×</button>
            </div>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-3"><dt className="text-slate-500">{t('admin.transactions.table.id')}</dt><dd className="font-mono text-slate-200">{selectedTx._id}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500">{t('admin.transactions.table.userId')}</dt><dd className="text-slate-200">{selectedTx.userId}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500">{t('admin.transactions.table.type')}</dt><dd className="text-slate-200">{getTransactionTypeLabel(selectedTx.type, selectedTx.paymentDetails)}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500">{t('admin.transactions.table.amount')}</dt><dd className={`font-semibold ${getAmountColor(selectedTx.amount)}`}>{selectedTx.amount > 0 ? '+' : ''}{Number(selectedTx.amount).toFixed(2)} {selectedTx.currency}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-slate-500">{t('admin.transactions.table.status')}</dt><dd><span className={`px-2 py-0.5 rounded-full text-xs ${getStatusBadgeColor(selectedTx.status)}`}>{t(`admin.transactions.status.${selectedTx.status}`)}</span></dd></div>
              {selectedTx.paymentMethod && <div className="flex justify-between gap-3"><dt className="text-slate-500">{t('admin.transactions.table.paymentMethod')}</dt><dd className="text-slate-200">{selectedTx.paymentMethod}</dd></div>}
              {selectedTx.notes && <div><dt className="text-slate-500 mb-1">{t('admin.transactions.table.notes')}</dt><dd className="text-slate-200 whitespace-pre-wrap">{scrubProviderBrands(selectedTx.notes)}</dd></div>}
              <div className="flex justify-between gap-3"><dt className="text-slate-500">{t('admin.transactions.table.created')}</dt><dd className="text-slate-300 text-xs">{formatDate(selectedTx.createdAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus')}</dd></div>
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}
